import React, { useState } from 'react';
import { useEditMode } from '@/contexts/EditModeContext';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  Cpu,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  AlertCircle,
  Info,
  Settings,
  Shield,
  Cog,
  Snowflake,
  Wrench,
  CheckCircle2,
  CheckCircle
} from 'lucide-react';
import { getSeverityColor } from '@/data/inspectionData';

const iconMap = {
  Shield: Shield,
  Settings: Settings,
  Cog: Cog,
  Snowflake: Snowflake
};

const SeverityIcon = ({ severity }) => {
  if (severity === 'critical') return <AlertTriangle className="h-4 w-4 text-destructive" />;
  if (severity === 'warning') return <AlertCircle className="h-4 w-4 text-warning" />;
  return <Info className="h-4 w-4 text-info" />;
};

const FaultCard = ({ fault, isEditMode }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const getSeverityClass = () => {
    if (fault.severity === 'critical') return 'obd-error-critical';
    if (fault.severity === 'warning') return 'obd-error-warning';
    return 'obd-error-info';
  };

  return (
    <div className={`obd-error-card ${getSeverityClass()}`}>
      <button 
        className="w-full flex items-start justify-between text-left"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-start gap-3 flex-1">
          <SeverityIcon severity={fault.severity} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-sm font-bold">{fault.code}</span>
              <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                {fault.status}
              </Badge>
              {fault.aiComponent && (
                <Badge variant="secondary" className="text-[10px] px-1.5 py-0 bg-blue-50 text-blue-700">
                  {fault.aiComponent}
                </Badge>
              )}
              {fault.aiSeverity && (
                <Badge 
                  variant="outline" 
                  className={`text-[10px] px-1.5 py-0 ${
                    fault.aiSeverity === 'CRITICAL' ? 'bg-red-50 text-red-700 border-red-200' :
                    fault.aiSeverity === 'IMPORTANT' ? 'bg-orange-50 text-orange-700 border-orange-200' :
                    'bg-green-50 text-green-700 border-green-200'
                  }`}
                >
                  {fault.aiSeverity}
                </Badge>
              )}
            </div>
            <p className={`text-sm text-muted-foreground mt-1 ${isEditMode ? 'edit-field' : ''}`}>
              {fault.description && !fault.description.includes('Generic Code')
                ? fault.description
                : 'Description Not Available with Manufacturer'}
            </p>
            {fault.aiReason && (
              <p className="text-xs text-blue-600 mt-1 italic">
                AI: {fault.aiReason}
              </p>
            )}
          </div>
        </div>
        {isExpanded ? (
          <ChevronUp className="h-4 w-4 text-muted-foreground flex-shrink-0 ml-2" />
        ) : (
          <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0 ml-2" />
        )}
      </button>
      
      {isExpanded && (
        <div className="mt-3 pt-3 border-t border-border/50 space-y-3">
          {/* ECU Source if available */}
          {fault.ecuSource && (
            <div className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">
              ECU: {fault.ecuSource}
            </div>
          )}
          
          {/* Possible Causes */}
          <div>
            <p className="text-xs font-semibold text-muted-foreground mb-1.5 flex items-center gap-1">
              <AlertCircle className="h-3 w-3" /> Possible Causes
            </p>
            <ul className="space-y-1">
              {(fault.possibleCauses || []).map((cause, idx) => (
                <li key={idx} className="text-xs text-muted-foreground flex items-start gap-2">
                  <span className="text-warning">•</span>
                  <span className={isEditMode ? 'edit-field' : ''}>{cause}</span>
                </li>
              ))}
            </ul>
          </div>
          
          {/* Symptoms */}
          <div>
            <p className="text-xs font-semibold text-muted-foreground mb-1.5 flex items-center gap-1">
              <Info className="h-3 w-3" /> Symptoms
            </p>
            <ul className="space-y-1">
              {(fault.symptoms || []).map((symptom, idx) => (
                <li key={idx} className="text-xs text-muted-foreground flex items-start gap-2">
                  <span className="text-info">•</span>
                  {symptom}
                </li>
              ))}
            </ul>
          </div>
          
          {/* Solutions */}
          <div>
            <p className="text-xs font-semibold text-muted-foreground mb-1.5 flex items-center gap-1">
              <Wrench className="h-3 w-3" /> Solutions
            </p>
            <ul className="space-y-1">
              {(fault.solutions || []).map((solution, idx) => (
                <li key={idx} className="text-xs text-muted-foreground flex items-start gap-2">
                  <span className="text-success">{idx + 1}.</span>
                  <span className={isEditMode ? 'edit-field' : ''}>{solution}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

const SystemGroup = ({ system, isEditMode }) => {
  // Default to closed - let user click to expand
  const [isExpanded, setIsExpanded] = useState(false);
  const Icon = iconMap[system.icon] || Settings;
  
  const criticalCount = system.faults.filter(f => f.severity === 'critical').length;
  const warningCount = system.faults.filter(f => f.severity === 'warning').length;
  const infoCount = system.faults.filter(f => f.severity === 'info').length;

  return (
    <div className="border border-border rounded-xl overflow-hidden">
      <button 
        className="w-full p-4 flex items-center justify-between bg-slate-100 hover:bg-slate-200 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-3">
          {/* Short name badge - matching Live Modal design */}
          <div className="h-10 w-10 rounded-lg bg-slate-700 flex items-center justify-center">
            <span className="text-white font-bold text-xs">
              {system.shortName || system.code || system.name?.substring(0, 3).toUpperCase()}
            </span>
          </div>
          <div className="text-left">
            <p className="font-semibold text-gray-900">{system.name}</p>
            <p className="text-xs text-gray-500">{system.faults.length} codes</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {criticalCount > 0 && (
            <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-700 border border-red-200">
              {criticalCount} Critical
            </span>
          )}
          {warningCount > 0 && (
            <span className="px-2 py-1 text-xs rounded-full bg-orange-100 text-orange-700 border border-orange-200">
              {warningCount} Important
            </span>
          )}
          {infoCount > 0 && (
            <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-600 border border-gray-200">
              {infoCount} Minor
            </span>
          )}
          {isExpanded ? (
            <ChevronUp className="h-5 w-5 text-gray-500 ml-2" />
          ) : (
            <ChevronDown className="h-5 w-5 text-gray-500 ml-2" />
          )}
        </div>
      </button>
      
      {isExpanded && (
        <div className="divide-y">
          {system.faults.map((fault, idx) => (
            <FaultCard key={idx} fault={fault} isEditMode={isEditMode} />
          ))}
        </div>
      )}
    </div>
  );
};

export function OBDReportMobile({ data }) {
  const { isEditMode } = useEditMode();
  
  // Handle missing or malformed data gracefully
  if (!data || !data.systems) {
    return (
      <section className="px-4 mt-4">
        <div className="mobile-card">
          <div className="mobile-card-header flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="p-1.5 rounded-lg bg-muted/50">
                <Cpu className="h-4 w-4 text-muted-foreground" />
              </div>
              <h2 className="font-semibold font-display">OBD-2 Diagnostics</h2>
            </div>
            <Badge variant="outline" className="bg-muted/50 text-muted-foreground">
              Not Available
            </Badge>
          </div>
          <div className="mobile-card-body">
            <div className="text-center py-8">
              <div className="w-12 h-12 rounded-full bg-muted/50 flex items-center justify-center mx-auto mb-3">
                <Cpu className="h-6 w-6 text-muted-foreground" />
              </div>
              <p className="font-medium text-muted-foreground">OBD Data Not Connected</p>
              <p className="text-sm text-muted-foreground mt-1">
                OBD-2 diagnostic data will appear here once connected
              </p>
            </div>
          </div>
        </div>
      </section>
    );
  }
  
  const hasErrors = (data.totalErrors || 0) > 0;

  // Calculate severity counts across all systems (matching Live Modal)
  const criticalTotal = (data.systems || []).reduce((sum, sys) => 
    sum + (sys.criticalCount || sys.faults?.filter(f => f.severity === 'critical').length || 0), 0);
  const importantTotal = (data.systems || []).reduce((sum, sys) => 
    sum + (sys.importantCount || sys.faults?.filter(f => f.severity === 'warning').length || 0), 0);
  const minorTotal = (data.systems || []).reduce((sum, sys) => 
    sum + (sys.nonImportantCount || sys.faults?.filter(f => f.severity === 'info').length || 0), 0);

  return (
    <section className="px-4 mt-4">
      <div className="mobile-card">
        <div className="mobile-card-header flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className={`p-1.5 rounded-lg ${hasErrors ? 'bg-destructive/10' : 'bg-success/10'}`}>
              <Cpu className={`h-4 w-4 ${hasErrors ? 'text-destructive' : 'text-success'}`} />
            </div>
            <h2 className="font-semibold font-display">OBD-2 Diagnostics</h2>
          </div>
          <Badge 
            variant="outline" 
            className={hasErrors 
              ? 'bg-destructive/10 text-destructive border-destructive/20' 
              : 'bg-success/10 text-success border-success/20'
            }
          >
            {data.totalErrors || 0} {(data.totalErrors || 0) === 1 ? 'Error' : 'Errors'}
          </Badge>
        </div>
        
        <div className="mobile-card-body">
          {hasErrors ? (
            <>
              {/* Summary Stats - Matching Live Modal Design */}
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl mb-4 flex-wrap">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <span className="text-sm font-medium text-green-700">Scan Done</span>
                </div>
                <div className="h-4 w-px bg-slate-300" />
                <div className="flex items-center gap-1.5">
                  <span className="px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-700 font-medium">
                    Critical ({criticalTotal})
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="px-2 py-0.5 text-xs rounded-full bg-orange-100 text-orange-700 font-medium">
                    Important ({importantTotal})
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600 font-medium">
                    Minor/Info ({minorTotal})
                  </span>
                </div>
              </div>

              {/* DTC Codes Header */}
              <div className="mb-3">
                <p className="text-sm font-semibold text-gray-700">
                  Diagnostic Trouble Codes (DTC) - {data.totalErrors || 0} codes
                </p>
              </div>
              
              {/* Critical Warning Alert */}
              {criticalTotal > 0 && (
                <div className="p-3 bg-destructive/5 border border-destructive/20 rounded-xl mb-4 flex items-center gap-3">
                  <AlertTriangle className="h-5 w-5 text-destructive flex-shrink-0" />
                  <p className="text-sm text-destructive">
                    <strong>{criticalTotal} critical</strong> issue{criticalTotal > 1 ? 's' : ''} require immediate attention
                  </p>
                </div>
              )}
              
              {/* System Groups - Grouped by Component/Module */}
              <div className="space-y-3">
                {(data.systems || []).map((system, idx) => (
                  <SystemGroup key={idx} system={system} isEditMode={isEditMode} />
                ))}
              </div>
            </>
          ) : (
            <div className="text-center py-8">
              <div className="w-12 h-12 rounded-full bg-success/10 flex items-center justify-center mx-auto mb-3">
                <CheckCircle2 className="h-6 w-6 text-success" />
              </div>
              <p className="font-medium">No Diagnostic Errors</p>
              <p className="text-sm text-muted-foreground mt-1">
                All systems functioning normally
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

export default OBDReportMobile;
